import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, ArrowUp, ArrowDown, Minus, X } from "@phosphor-icons/react";

// /panelist/watchlist — list of brands the user is watching.
// Mirrors Glassdoor's "following" page: name, current rating, 30-day delta vs all-time.
export default function Watchlist() {
  const [items, setItems] = useState(() => api.getCached("/panelist/watchlist")?.items || null);

  const load = () => {
    api.get("/panelist/watchlist")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const remove = async (slug, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/panelist/watchlist/${slug}`);
      toast.success("Removed from watchlist");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  if (items === null) return <div className="p-8 text-neutral-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="watchlist-page">
      <div className="pb-4 mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1">PANEL · WATCHLIST</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">Brands you're watching</h1>
        <p className="text-sm text-neutral-500 mt-2">Track the sentiment of brands, people and policies you care about. Click ★ on any sentiment page to add or remove.</p>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center" data-testid="watchlist-empty">
          <Star size={32} className="mx-auto text-neutral-300 mb-3" />
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900 mb-1">Your watchlist is empty</h2>
          <p className="text-sm text-neutral-500 mb-4">Browse Public Sentiment and click the ★ button on any brand to add them.</p>
          <Link to="/panelist/sentiment" className="inline-block bg-mango text-white rounded-full px-5 py-2.5 font-bold hover:bg-mango/90 transition-colors" data-testid="watchlist-browse">Browse Sentiment</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watchlist-grid">
          {items.map((it) => {
            const delta = (it.avg_30d != null && it.avg_rating != null) ? Math.round((it.avg_30d - it.avg_rating) * 100) / 100 : null;
            return (
              <Link
                key={it.id}
                to={`/panelist/sentiment/${it.slug}`}
                data-testid={`watchlist-item-${it.slug}`}
                className="group border border-neutral-200 rounded-2xl bg-white p-5 shadow-sm hover:shadow-md hover:border-mango/40 transition-all relative"
              >
                <button
                  onClick={(e) => remove(it.slug, e)}
                  data-testid={`watchlist-remove-${it.slug}`}
                  title="Remove from watchlist"
                  className="absolute top-3 right-3 w-7 h-7 rounded-full inline-flex items-center justify-center border border-neutral-200 bg-white text-neutral-400 hover:border-red-300 hover:text-red-500 transition-colors"
                >
                  <X size={14} />
                </button>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-neutral-100 overflow-hidden flex-shrink-0">
                    {it.photo_url ? <img src={it.photo_url} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-2xl text-neutral-300">{it.name[0]}</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold tracking-tight truncate pr-6 text-neutral-900">{it.name}</div>
                    <div className="text-xs text-neutral-400 mt-1">{it.category_slug}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-lg font-bold text-neutral-900">{it.avg_rating ?? "—"}</span>
                      <Star weight="fill" size={14} className="text-mango" />
                      <span className="text-xs text-neutral-400">· {it.total_votes}</span>
                    </div>
                    <TrendPill delta={delta} avg30={it.avg_30d} n30={it.n_30d} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrendPill({ delta, avg30, n30 }) {
  if (avg30 == null || n30 === 0) {
    return <div className="mt-2 text-[11px] text-neutral-400">No ratings in last 30 days</div>;
  }
  const up = delta != null && delta > 0.05;
  const down = delta != null && delta < -0.05;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const color = up ? "text-emerald-600" : down ? "text-rose-600" : "text-neutral-400";
  const sign = delta != null && delta > 0 ? "+" : "";
  return (
    <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${color}`} data-testid="watchlist-trend">
      <Icon size={11} weight="bold" />
      {delta != null ? `${sign}${delta.toFixed(2)}` : "—"} 30d · n={n30}
    </div>
  );
}
